import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import OnlineOrders from "@/pages/OnlineOrders";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import QuoteBuilder from "@/pages/QuoteBuilder";
import QuotePreview from "@/pages/QuotePreview";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Tickets from "@/pages/Tickets";
import Rma from "@/pages/Rma";
import Workflows from "@/pages/Workflows";
import LeadFormPage from "@/pages/LeadFormPage";
import Gallery from "@/pages/Gallery";
import RoleManager from "@/pages/RoleManager";
import Settings from "@/pages/Settings";
import Quotes from "@/pages/Quotes";
import Forms from "@/pages/Forms";
import B2B from "@/pages/B2B";
import B2BDocument from "@/pages/B2BDocument";
import TrackBuild from "@/pages/TrackBuild";
import QuoteDocument from "@/pages/QuoteDocument";
import ChangePassword from "@/pages/ChangePassword";
import { ROUTE_PERMS, can, homeFor } from "@/lib/roles";

function RoleRoute({ base, children }) {
  const { user } = useAuth();
  return can(user, ROUTE_PERMS[base]) ? children : <Navigate to={homeFor(user)} replace />;
}
const guard = (base, el) => <RoleRoute base={base}>{el}</RoleRoute>;

function Protected({ children, forcedOk = false }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword && !forcedOk) return <Navigate to="/change-password" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/lead-form" element={<LeadFormPage />} />
            <Route path="/quote-preview/:token" element={<QuotePreview />} />
            <Route path="/track-build/:token" element={<TrackBuild />} />
            <Route path="/gallery/:token" element={<Gallery />} />
            <Route path="/change-password" element={<Protected forcedOk><ChangePassword /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={guard("/dashboard", <Dashboard />)} />
              <Route path="/inventory" element={guard("/inventory", <Inventory />)} />
              <Route path="/online-orders" element={guard("/online-orders", <OnlineOrders />)} />
              <Route path="/leads" element={guard("/leads", <Leads />)} />
              <Route path="/leads/:id" element={guard("/leads", <LeadDetail />)} />
              <Route path="/leads/:id/quote-builder" element={guard("/leads", <QuoteBuilder />)} />
              <Route path="/b2b" element={guard("/b2b", <B2B />)} />
              <Route path="/b2b/orders/:oid/document" element={guard("/b2b", <B2BDocument />)} />
              <Route path="/forms" element={guard("/forms", <Forms />)} />
              <Route path="/quotes/builder" element={guard("/quotes", <QuoteBuilder />)} />
              <Route path="/quotes/:qid/document" element={guard("/quotes", <QuoteDocument />)} />
              <Route path="/quotes" element={guard("/quotes", <Quotes />)} />
              <Route path="/projects" element={guard("/projects", <Projects />)} />
              <Route path="/projects/:id" element={guard("/projects", <ProjectDetail />)} />
              <Route path="/projects/:id/order-confirmation" element={guard("/projects", <OrderConfirmation />)} />
              <Route path="/tickets" element={guard("/tickets", <Tickets />)} />
              <Route path="/rma" element={guard("/rma", <Rma />)} />
              <Route path="/settings/workflows" element={guard("/settings/workflows", <Workflows />)} />
              <Route path="/settings/users" element={guard("/settings/users", <RoleManager />)} />
              <Route path="/settings" element={guard("/settings", <Settings />)} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}
